/**
 * Multi-screen detection and management utilities
 * 
 * Uses the Window Management API to detect and work with multiple screens.
 * Falls back gracefully when the API is not available or permission is denied.
 */

// Type definitions for Window Management API (not yet in standard lib.dom.d.ts)
export interface ScreenDetailed extends Screen {
  availLeft: number;
  availTop: number;
  isPrimary: boolean;
}

interface ScreenDetails {
  screens: ScreenDetailed[];
  currentScreen: ScreenDetailed;
}

/**
 * Check if Window Management API is available
 */
export const hasWindowManagementAPI = (): boolean => {
  return 'getScreenDetails' in window;
};

/**
 * Check if user has multiple screens (without requiring permission)
 */
export const hasMultipleScreens = (): boolean => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (window.screen as any).isExtended === true;
};

/**
 * Get a different screen from the one the main window is on.
 * Returns null if:
 * - Window Management API not supported
 * - User only has one screen
 * - User denies permission
 */
export const getOtherScreen = async (): Promise<ScreenDetailed | null> => {
  // Don't prompt if API not available or user only has one screen
  if (!hasWindowManagementAPI() || !hasMultipleScreens()) {
    return null;
  }

  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const screenDetails: ScreenDetails = await (window as any).getScreenDetails();
    const currentScreen = screenDetails.currentScreen;
    
    // Find a screen that's different from the current one
    const otherScreen = screenDetails.screens.find(
      (screen: ScreenDetailed) => 
        screen.availLeft !== currentScreen.availLeft || 
        screen.availTop !== currentScreen.availTop
    );
    return otherScreen || null;
  } catch {
    // User denied permission or other error - fall back gracefully
    return null;
  }
};

/**
 * @deprecated Use getOtherScreen instead - it finds a screen different from the current one
 */
export const getSecondaryScreen = getOtherScreen;
